/**
 * What a picture the trainer offers as a cover becomes before it is sent — and when it is refused.
 *
 * ── Why this is `shared/` rather than the exercise creator's own ────────────────────────────
 * Its second consumer is `features/create-plan`'s cover picker, and one `features/` slice may not
 * import another (`eslint.config.js`, `boundaries/dependencies`). The rules did not change when
 * they moved: the same class the phone encodes, and the same three refusals — only their wording
 * left `exerciseLibrary.creator.media` for the neutral `media` namespace, since a plan cover is
 * refused for exactly the same reasons and by the same sentences.
 *
 * ── The backend reads JPEG and WebP, and nothing else ───────────────────────────────────────
 * `ImageSanitizer` (`train-with-me/Modules/Media/Services/ImageSanitizer.cs`) accepts only
 * `image/jpeg` and `image/webp`; anything else is a 400 `media_unsupported_type`. An earlier revision
 * of this file mirrored the types instead and allowed PNG, so a PNG cover was refused AFTER the save
 * and the trainer saw only „Brak okładki” (owner, 18 September 2026).
 *
 * So the web does what the phone does (`shared/media/compressImage.ts`, `imageClasses.ts`
 * `cover: { maxEdge: 1600, quality: 0.8 }`): ANY picture the browser can decode is scaled down so
 * its longest edge is at most 1600 px — never up — and re-encoded as JPEG at 0.8, which also drops
 * its EXIF. The 10 MiB cap (`MediaClassMap`'s `ExerciseCover`) then applies to the RESULT.
 *
 * This module holds the decisions; `lib/compressCover` does the canvas work.
 */

import { longEdgeScale, reencodedFileName } from './reencodedFile';

/** `MediaClassMap`'s `ExerciseCover` — `10 * OneMebibyte`, applied to the ENCODED result. */
export const MAX_COVER_BYTES = 10 * 1024 * 1024;

/** The phone's cover class: the longest edge, and the JPEG quality it is encoded at. */
export const COVER_MAX_EDGE = 1600;
export const COVER_QUALITY = 0.8;

/** What the encoded cover is sent as — the one type both clients send. */
export const COVER_OUTPUT_TYPE = 'image/jpeg';

/** What the file chooser offers: any picture, since every one is re-encoded. */
export const COVER_ACCEPT = 'image/*';

/**
 * Why a picture cannot be a cover.
 *
 * · `notAnImage` — the browser does not call it a picture at all (an empty or non-`image/` type).
 * · `undecodable` — it says it is one and the browser cannot draw it (a HEIC in Chrome, a broken file).
 * · `tooLarge` — even re-encoded it is over the cap.
 */
export type CoverRefusal = 'notAnImage' | 'undecodable' | 'tooLarge';

/** The refusal a file earns BEFORE anything is decoded, or `null` when it is worth decoding. */
export function coverTypeRefusal(file: { readonly type: string }): CoverRefusal | null {
  return file.type.toLowerCase().startsWith('image/') ? null : 'notAnImage';
}

/** The refusal the ENCODED result earns, or `null` when it may be sent. */
export function encodedCoverRefusal(encoded: { readonly size: number }): CoverRefusal | null {
  return encoded.size > MAX_COVER_BYTES ? 'tooLarge' : null;
}

/**
 * The size a picture is drawn at: the longest edge at most `COVER_MAX_EDGE`, the proportions kept,
 * and never larger than it already is. Whole pixels, and never zero.
 */
export function coverTargetSize(
  width: number,
  height: number,
): { readonly width: number; readonly height: number } {
  const scale = longEdgeScale(width, height, COVER_MAX_EDGE);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** The encoded file's name: the trainer's own, with the extension the bytes now have. */
export function coverFileName(original: string): string {
  return reencodedFileName(original, 'okladka', 'jpg');
}

/** The sentence a refusal reads as, as a key. One per refusal, so copy is chosen, never composed. */
export function coverRefusalKey(refusal: CoverRefusal): string {
  switch (refusal) {
    case 'notAnImage':
      return 'web:media.coverNotAnImage';
    case 'undecodable':
      return 'web:media.coverUndecodable';
    case 'tooLarge':
      return 'web:media.coverTooLarge';
  }
}
